import { computed, markRaw, reactive, ref, shallowRef } from 'vue';

import FeedbackRepository from '@/features/feedback/api/FeedbackRepository';
import { useElementPicker } from '@/features/feedback/composables/useElementPicker';
import { useRegionSelector } from '@/features/feedback/composables/useRegionSelector';
import {
  collectClientContext,
  collectPageContext,
  describeElement,
  describeSubject,
  elementFromSelection,
} from '@/features/feedback/domain/pageContext';
import {
  CaptureCancelled,
  CaptureUnsupported,
  canvasFromFile,
  captureRegionFrame,
  captureViewportFrame,
  encodeUnderLimit,
  isDisplayCaptureSupported,
  renderAnnotated,
} from '@/features/feedback/domain/screenshot';

export const MAX_SCREENSHOTS = 3;
const MAX_SELECTION_CHARS = 2000;

/**
 * The feedback draft. Module-level on purpose: closing the panel, switching to
 * the element picker, capturing a screenshot and a failed submit must all
 * leave the typed description and the attachments exactly where they were.
 * Only a successful submit clears it.
 */
const draft = reactive({
  kind: 'issue',
  description: '',
  selectedText: '',
  element: null,
  subject: null,
  screenshots: [], // { id, canvas, shapes: [], crop: null }
});

const open = ref(false);
const hidden = ref(false); // panel temporarily out of the way (picking/capturing)
// Floating launcher dismissed for this session. Module-level and never
// persisted, so a reload (or the "Feedback" menu item, which reopens the
// panel) brings the button straight back — dismissing it is a per-visit
// convenience, not a standing preference.
const dismissed = ref(false);
const submitting = ref(false);
const error = ref('');
const lastReference = ref('');
const editingScreenshotId = ref('');

/** The most recent non-empty page selection, kept even after focus moves. */
const trackedSelection = ref('');
/** The element that selection sat in, so "select text" also answers "where". */
const trackedSelectionElement = shallowRef(null);
const pickedElement = shallowRef(null);
/**
 * How the current element got here: `picker` if the user aimed at it,
 * `selection` if we inferred it from selected text. Only an inferred one may
 * be replaced or dropped automatically — a deliberate pick outranks us.
 */
const elementSource = ref('');

let submissionId = newSubmissionId();
let selectionBound = false;

function newSubmissionId() {
  const uuid = globalThis.crypto?.randomUUID?.();
  return (uuid || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9_-]/g, '');
}

function readSelection() {
  const text = window.getSelection?.()?.toString?.() || '';
  const trimmed = text.trim();
  // Only remember non-empty selections: opening the panel collapses the
  // caret, and that must not erase what the user had highlighted first.
  if (!trimmed) return;
  trackedSelection.value = trimmed.slice(0, MAX_SELECTION_CHARS);
  trackedSelectionElement.value = elementFromSelection();
}

/** Bind once; the launcher mounts for the session, so there is nothing to unbind. */
export function bindSelectionTracking() {
  if (selectionBound || typeof document === 'undefined') return;
  document.addEventListener('selectionchange', readSelection);
  selectionBound = true;
}

export function useFeedbackReporter() {
  const hasSelectionAvailable = computed(
    () => Boolean(trackedSelection.value) && trackedSelection.value !== draft.selectedText
  );
  const canSubmit = computed(() => draft.description.trim().length >= 5 && !submitting.value);
  const captureSupported = computed(() => isDisplayCaptureSupported());

  function openPanel() {
    // The selection is re-read from the page on every open, and a stale one is
    // dropped rather than carried over. It used to persist with the rest of
    // the draft, which meant giving feedback on a second thing started with
    // the first thing's quote still attached and had to be cleared by hand.
    // The description and the screenshots still survive a close — those are
    // work; a selection is just where you were pointing.
    const previous = trackedSelection.value;
    trackedSelection.value = '';
    trackedSelectionElement.value = null;
    readSelection();
    const live = trackedSelection.value;
    draft.selectedText = live;
    // Selecting text is also a statement about *where*: adopt the element it
    // sits in, so page, element and quote come from one gesture. A hand-picked
    // element wins — the user aimed at that on purpose.
    const inferred = elementSource.value !== 'picker';
    if (live && trackedSelectionElement.value && inferred) {
      setElement(trackedSelectionElement.value, 'selection');
    } else if (!live && previous && inferred && draft.element) {
      clearElement();
    }
    error.value = '';
    lastReference.value = '';
    open.value = true;
    hidden.value = false;
    dismissed.value = false;
  }

  function closePanel() {
    open.value = false;
    hidden.value = false;
    editingScreenshotId.value = '';
  }

  function dismissLauncher() {
    dismissed.value = true;
  }

  function useCurrentSelection() {
    readSelection();
    draft.selectedText = trackedSelection.value;
    if (trackedSelectionElement.value && elementSource.value !== 'picker') {
      setElement(trackedSelectionElement.value, 'selection');
    }
  }

  function clearSelectedText() {
    draft.selectedText = '';
  }

  /**
   * Hand control to the element picker. The panel hides itself while this
   * runs (see `pickerActive` in the launcher) so it cannot cover the target;
   * the draft is untouched whether a pick lands or the user presses Escape.
   */
  async function startPicking() {
    const element = await useElementPicker().start();
    if (element) setElement(element);
    return element;
  }

  function screenshotById(id) {
    return draft.screenshots.find((shot) => shot.id === id) || null;
  }

  /** Open the full-screen editor on one shot. */
  function editScreenshot(id) {
    editingScreenshotId.value = id;
  }

  function closeEditor() {
    editingScreenshotId.value = '';
  }

  function setElement(element, source = 'picker') {
    pickedElement.value = element;
    elementSource.value = source;
    draft.element = describeElement(element);
    draft.subject = describeSubject(element);
  }

  function clearElement() {
    pickedElement.value = null;
    elementSource.value = '';
    draft.element = null;
    draft.subject = null;
  }

  function addScreenshotCanvas(canvas) {
    if (draft.screenshots.length >= MAX_SCREENSHOTS) {
      error.value = 'limit';
      return null;
    }
    // markRaw: the draft is `reactive`, which would otherwise wrap the canvas
    // in a Proxy — and a Proxy of a canvas fails `drawImage`'s platform-object
    // brand check, so the annotated upload would throw instead of rendering.
    const entry = { id: newSubmissionId(), canvas: markRaw(canvas), shapes: [], crop: null };
    draft.screenshots.push(entry);
    editingScreenshotId.value = entry.id;
    return entry;
  }

  function removeScreenshot(id) {
    const index = draft.screenshots.findIndex((shot) => shot.id === id);
    if (index >= 0) draft.screenshots.splice(index, 1);
    if (editingScreenshotId.value === id) editingScreenshotId.value = '';
  }

  /**
   * Native one-frame capture. The panel and the floating button are hidden
   * first so they never end up in the shot, and restored on every exit path.
   */
  async function captureScreenshot() {
    error.value = '';
    hidden.value = true;
    // Two frames: one for Vue to apply `hidden`, one for the browser to paint it.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      addScreenshotCanvas(await captureViewportFrame());
    } catch (exception) {
      if (exception instanceof CaptureCancelled) error.value = 'capture_cancelled';
      else if (exception instanceof CaptureUnsupported) error.value = 'capture_unsupported';
      else error.value = 'capture_failed';
    } finally {
      hidden.value = false;
    }
  }

  /**
   * Draw a box on the page, then capture and keep only that box.
   *
   * Order matters: the rectangle is drawn on the live page BEFORE the picker
   * opens, so the user is aiming at the thing itself rather than at a frozen
   * copy of it. The overlay and the panel are gone before the frame is
   * grabbed, so the crosshair never lands in the shot, and the two rAFs are
   * well inside the transient-activation window that `getDisplayMedia`
   * requires — the finishing click is the gesture that authorises it.
   */
  async function captureRegion() {
    error.value = '';
    hidden.value = true;
    const region = await useRegionSelector().start();
    if (!region) {
      hidden.value = false;
      return;
    }
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      addScreenshotCanvas(await captureRegionFrame(region));
    } catch (exception) {
      if (exception instanceof CaptureCancelled) error.value = 'capture_cancelled';
      else if (exception instanceof CaptureUnsupported) error.value = 'capture_unsupported';
      else error.value = 'capture_failed';
    } finally {
      hidden.value = false;
    }
  }

  async function addScreenshotFile(file) {
    if (!file || !file.type?.startsWith('image/')) return;
    error.value = '';
    try {
      addScreenshotCanvas(await canvasFromFile(file));
    } catch {
      error.value = 'capture_failed';
    }
  }

  function handlePaste(event) {
    const item = Array.from(event.clipboardData?.items || []).find((entry) =>
      entry.type?.startsWith('image/')
    );
    if (!item) return;
    event.preventDefault();
    addScreenshotFile(item.getAsFile());
  }

  async function submit(route = null) {
    if (!canSubmit.value) return null;
    submitting.value = true;
    error.value = '';
    try {
      // Annotations are baked in here, once, and the result is what uploads —
      // the untouched capture never leaves this tab.
      const blobs = [];
      for (const shot of draft.screenshots) {
        const blob = await encodeUnderLimit(renderAnnotated(shot.canvas, shot.shapes, shot.crop));
        if (blob) blobs.push(blob);
      }

      const { data } = await FeedbackRepository.submit(
        {
          submission_id: submissionId,
          kind: draft.kind,
          description: draft.description.trim(),
          selected_text: draft.selectedText || null,
          element: draft.element,
          subject: draft.subject,
          page: collectPageContext(route),
          client: collectClientContext(),
          app_version: process.env.VUE_APP_COMMIT_HASH || null,
        },
        blobs
      );

      lastReference.value = data.reference;
      resetDraft();
      return data;
    } catch (exception) {
      // The draft is untouched on failure — retrying reuses the same
      // submission id, so a retry after a timeout cannot create a second report.
      error.value = exception?.response?.status === 403 ? 'forbidden' : 'submit_failed';
      return null;
    } finally {
      submitting.value = false;
    }
  }

  function resetDraft() {
    draft.kind = 'issue';
    draft.description = '';
    draft.selectedText = '';
    draft.element = null;
    draft.subject = null;
    draft.screenshots.splice(0);
    pickedElement.value = null;
    elementSource.value = '';
    trackedSelection.value = '';
    trackedSelectionElement.value = null;
    editingScreenshotId.value = '';
    submissionId = newSubmissionId();
  }

  return {
    draft,
    open,
    hidden,
    dismissed,
    submitting,
    error,
    lastReference,
    editingScreenshotId,
    pickedElement,
    canSubmit,
    captureSupported,
    hasSelectionAvailable,
    trackedSelection,
    openPanel,
    closePanel,
    dismissLauncher,
    useCurrentSelection,
    clearSelectedText,
    setElement,
    startPicking,
    clearElement,
    screenshotById,
    editScreenshot,
    closeEditor,
    captureScreenshot,
    captureRegion,
    addScreenshotFile,
    handlePaste,
    removeScreenshot,
    submit,
    resetDraft,
  };
}
